/**
 * Models Configuration
 * 
 * This file contains all model definitions and their properties.
 * To add new models, simply add entries to the MODELS array.
 * 
 * Structure:
 * - code: The model code/identifier (e.g., "4031-OTI")
 * - label: Display name for the model
 * - particular: The particular value for this model (auto-populated in ProductSpecs)
 * - accessories: Optional. If provided, this model will show the accessories field
 * - defaults: Default values for specification fields (auto-populated when model is selected)
 */

export const MODELS = [
  {
    code: '4031-OTI',
    label: '4031-OTI',
    particular: 'Oil Temperature Indicator',
    accessories: null, // No accessories for this model
    defaults: {
      range: "0 - 150 °C",
      noOfSwitches: "2",
      dialGraduation: "2 °C",
      switchType: "Micro Switch",
      accuracy: "1.5% of FSD",
      switchRating: "5A @ 250VAC & 0.5A @ 250V DC",
      capillaryLength: "Meters",
      switchDifferential: "4 ± 2°C",
      degreeOfProtection: "IP-55",
    },
  },
  {
    code: '4031-WTI',
    label: '4031-WTI',
    particular: 'Winding Temperature Indicator',
    accessories: null,
    defaults: {
      range: "0 - 150 °C",
      noOfSwitches: "2",
      dialGraduation: "2 °C",
      switchType: "Micro Switch",
      accuracy: "1.5% of FSD",
      switchRating: "5A @ 250VAC & 0.5A @ 250V DC",
      capillaryLength: "Meters",
      switchDifferential: "4 ± 2°C",
      degreeOfProtection: "IP-55",
    },
  },
  {
    code: '4031H-OTI',
    label: '4031H-OTI',
    particular: 'Oil Temperature Indicator',
    accessories: "External Current Converter Unit With 2no's of 4-20mA output +RI",
    defaults: {
      range: "0 - 150 °C",
      noOfSwitches: "2",
      dialGraduation: "2 °C",
      switchType: "Micro Switch",
      accuracy: "1.5% of FSD",
      switchRating: "5A @ 250VAC & 0.5A @ 250V DC",
      capillaryLength: "Meters",
      switchDifferential: "4 ± 2°C",
      degreeOfProtection: "IP-55",
    },
  },
  {
    code: '4031H-WTI',
    label: '4031H-WTI',
    particular: 'Winding Temperature Indicator',
    accessories: "External Current Converter Unit With 2no's of 4-20mA output +RI",
    defaults: {
      range: "0 - 150 °C",
      noOfSwitches: "2",
      dialGraduation: "2 °C",
      switchType: "Micro Switch",
      accuracy: "1.5% of FSD",
      switchRating: "5A @ 250VAC & 0.5A @ 250V DC",
      capillaryLength: "Meters",
      switchDifferential: "4 ± 2°C",
      degreeOfProtection: "IP-55",
    },
  },
  // Add more models here as needed
  // Example:
  // {
  //   code: '4032-OTI',
  //   label: '4032-OTI',
  //   accessories: "Some accessory description",
  // },
];

/**
 * Get model by code
 * @param {string} code - Model code
 * @returns {Object|null} Model configuration or null if not found
 */
export const getModelByCode = (code) => {
  return MODELS.find(model => model.code === code) || null;
};

/**
 * Get accessories for a model
 * @param {string} code - Model code
 * @returns {string|null} Accessories description or null if no accessories
 */
export const getModelAccessories = (code) => {
  const model = getModelByCode(code);
  return model?.accessories || null;
};

/**
 * Check if a model has accessories
 * @param {string} code - Model code
 * @returns {boolean} True if model has accessories
 */
export const hasModelAccessories = (code) => {
  return getModelAccessories(code) !== null;
};

/**
 * Get particular value for a model
 * @param {string} code - Model code
 * @returns {string|null} Particular value or null if not found
 */
export const getModelParticular = (code) => {
  const model = getModelByCode(code);
  return model?.particular || null;
};

/**
 * Get default specification values for a model
 * @param {string} code - Model code
 * @returns {Object|null} Default specification values or null if not found
 */
export const getModelDefaults = (code) => {
  const model = getModelByCode(code);
  return model?.defaults || null;
};

/**
 * Get all model codes for dropdown
 * @returns {Array<string>} Array of model codes
 */
export const getModelCodes = () => {
  return MODELS.map(model => model.code);
};

/**
 * Get all model options for select dropdown
 * @returns {Array<{value: string, label: string}>} Array of model options
 */
export const getModelOptions = () => {
  return MODELS.map(model => ({
    value: model.code,
    label: model.label,
  }));
};


